import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { User, newUser, Email, Phone, Address, newEmail, newPhone, newAddress } from "../../../../model";
import { ClickableIcon } from "../../../../components/ui/Icon";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CustomPhoneInput from "../../../../components/ui/CustomPhoneInput";
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'
import { SingleSelectChip } from "../../../../components/ui/DropdownSelect";
import { SingleDatePicker } from "../../../../components/ui/SingleDatePicker";

interface UserFormProps {
  user: User;
  error?: string;
  autocomplete?: boolean; // toggle autocomplete, nice for managers
}

export interface UserFormRef {
  valid: () => boolean;
  getUser: () => User;
}

export const UserForm = forwardRef((props: UserFormProps, ref) => {
  const { error, autocomplete = true } = props;
  const [user, setUser] = useState<User>(newUser());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setUser(props.user);
  }, [props.user]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(user) as (keyof User)[]) {
      if (typeof user[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getUser = () => {
    return user;
  }

  // Expose the valid function to the parent, and a method to get the whole user object
  useImperativeHandle(ref, () => ({
    valid,
    getUser,
  }));

  const validateBaseField = (field: keyof User): string => {
    if (field === "first_name") {
      if (!user.first_name?.trim()) return "Fornavn kan ikke være tom";
    } else if (field === "last_name") {
      if (!user.last_name?.trim()) return "Etternavn kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof User, value: any) => {
    setUser((prev) => ({ ...prev, [field]: value }));

    // setTouched((prev) => ({
    //   ...prev,
    //   base: { ...prev.base, [field]: true },
    // }));
  };

  const handleBaseBlur = (field: keyof User) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <TextField
            label="Brukernavn"
            required
            fullWidth
            value={user.username}
            onChange={(e) => handleBaseChange("username", e.target.value)}
            variant="outlined"
            error={validateBaseField("username") !== "" && (submitted || touched.base.username)}
            helperText={(submitted || touched.base.username) ? validateBaseField("username") : ""}
            onBlur={() => handleBaseBlur("username")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Fornavn"
            required
            fullWidth
            autoComplete={autocomplete ? "given-name" : ""}
            value={user.first_name}
            onChange={(e) => handleBaseChange("first_name", e.target.value)}
            variant="outlined"
            error={validateBaseField("first_name") !== "" && (submitted || touched.base.first_name)}
            helperText={(submitted || touched.base.first_name) ? validateBaseField("first_name") : ""}
            onBlur={() => handleBaseBlur("first_name")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Etternavn"
            required
            fullWidth
            autoComplete={autocomplete ? "family-name" : ""}
            value={user.last_name}
            onChange={(e) => handleBaseChange("last_name", e.target.value)}
            variant="outlined"
            error={validateBaseField("last_name") !== "" && (submitted || touched.base.last_name)}
            helperText={(submitted || touched.base.last_name) ? validateBaseField("last_name") : ""}
            onBlur={() => handleBaseBlur("last_name")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Sett nytt passord"
            fullWidth
            autoComplete={autocomplete ? "password" : ""}
            value={user.password}
            onChange={(e) => handleBaseChange("password", e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SingleDatePicker
            label="Passord utløper"
            fullWidth
            minDate={new Date()}
            date={user.pw_expiredate}
            onChange={(date) => handleBaseChange("pw_expiredate", date)}
          />
        </Grid>
      </Grid>
    </form>
  )
});